import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, List, Rows3, DoorOpen, Eye } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { tidTilMin, SSTATUS, FORMAT_LABEL } from "./eventosUtils";
import { ReadinessBadge } from "./Readiness";
import SessionDrawer from "./SessionDrawer";
import SpacesModal from "./SpacesModal";

const feil = (e) => toast.error(formatApiError(e?.response?.data?.detail) || "Noe gikk galt");
const START_MIN = 8 * 60;
const SLUTT_MIN = 19 * 60;

const Tidslinje = ({ sessions, spaces, onOpen }) => {
  const planlagte = sessions.filter((s) => s.spaceId && tidTilMin(s.start) != null && tidTilMin(s.end) != null);
  const uplasserte = sessions.filter((s) => !planlagte.includes(s));
  const timer = [];
  for (let m = START_MIN; m <= SLUTT_MIN; m += 60) timer.push(m);
  const radFor = (min) => Math.round((min - START_MIN) / 5) + 2;

  return (
    <div data-testid="program-tidslinje">
      <div className="adm-card overflow-x-auto p-4">
        <div
          className="grid min-w-[900px]"
          style={{
            gridTemplateColumns: `56px repeat(${spaces.length}, minmax(150px, 1fr))`,
            gridTemplateRows: `28px repeat(${(SLUTT_MIN - START_MIN) / 5}, 9px)`,
          }}
        >
          <div />
          {spaces.map((sp, i) => (
            <div key={sp.id} className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 px-2 truncate" style={{ gridColumn: i + 2, gridRow: 1 }}>
              {sp.name}
            </div>
          ))}
          {timer.map((m) => (
            <div key={m} className="text-[10px] tabular-nums text-neutral-300 pr-2 text-right border-t border-[#F1EEE7]" style={{ gridColumn: 1, gridRow: `${radFor(m)} / span 12` }}>
              {`${String(Math.floor(m / 60)).padStart(2, "0")}:00`}
            </div>
          ))}
          {timer.map((m) =>
            spaces.map((sp, i) => (
              <div key={`${m}-${sp.id}`} className="border-t border-[#F1EEE7]" style={{ gridColumn: i + 2, gridRow: `${radFor(m)} / span 12` }} />
            ))
          )}
          {planlagte.map((s) => {
            const kol = spaces.findIndex((sp) => sp.id === s.spaceId);
            if (kol < 0) return null;
            const r1 = radFor(Math.max(START_MIN, tidTilMin(s.start)));
            const r2 = radFor(Math.min(SLUTT_MIN, Math.max(tidTilMin(s.end), tidTilMin(s.start) + 15)));
            const konflikt = (s.conflicts || []).length > 0;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onOpen(s.id)}
                className={`text-left rounded-lg border px-2 py-1 m-px overflow-hidden transition-all hover:shadow-md hover:z-10 ${
                  konflikt ? "bg-red-50 border-red-300" : s.published ? "bg-white border-[#DED9CD]" : "bg-[#F7F5F0] border-dashed border-[#CFCAC0]"
                }`}
                style={{ gridColumn: kol + 2, gridRow: `${r1} / ${r2}` }}
                data-testid={`tidslinje-sesjon-${s.id}`}
                title={`${s.start}–${s.end} · ${s.internalTitle}`}
              >
                <p className="text-[10px] tabular-nums text-neutral-400 leading-tight">{s.start}–{s.end}</p>
                <p className="text-[11px] font-medium text-neutral-900 leading-tight truncate">{s.internalTitle}</p>
                {!s.published && <p className="text-[9px] uppercase tracking-wider text-neutral-400 mt-0.5">utkast</p>}
              </button>
            );
          })}
        </div>
      </div>
      {uplasserte.length > 0 && (
        <div className="mt-4" data-testid="program-uplassert">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400 mb-2">
            Ikke plassert i tidslinjen ({uplasserte.length}) — mangler rom eller sluttid
          </p>
          <div className="flex flex-wrap gap-2">
            {uplasserte.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => onOpen(s.id)}
                className="text-[12px] bg-white border border-dashed border-[#CFCAC0] rounded-lg px-3 py-1.5 hover:border-neutral-500 transition-colors"
                data-testid={`uplassert-${s.id}`}
              >
                {s.start ? `${s.start} · ` : ""}{s.internalTitle}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function ProgramOS() {
  const [data, setData] = useState(null);
  const [visning, setVisning] = useState("tidslinje");
  const [aapen, setAapen] = useState(null);
  const [visSpaces, setVisSpaces] = useState(false);
  const [kunPublisert, setKunPublisert] = useState(false);
  const aktor = localStorage.getItem("nas_aktor") || "Admin";

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/os/program");
      setData(d);
    } catch (e) {
      feil(e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (sid, p) => {
    try {
      await api.patch(`/admin/os/sessions/${sid}`, { ...p, actor: aktor });
      await load();
    } catch (e) {
      feil(e);
    }
  };

  const nySession = async () => {
    const tittel = window.prompt("Intern arbeidstittel for ny session:");
    if (!tittel || !tittel.trim()) return;
    try {
      const { data: r } = await api.post("/admin/os/sessions", { internalTitle: tittel.trim(), actor: aktor });
      await load();
      setAapen(r.item.id);
      toast.success("Session opprettet");
    } catch (e) {
      feil(e);
    }
  };

  const slett = async (sid) => {
    if (!window.confirm("Slette sessionen?")) return;
    try {
      await api.delete(`/admin/os/sessions/${sid}`);
      setAapen(null);
      await load();
      toast.success("Slettet");
    } catch (e) {
      feil(e);
    }
  };

  const nySpace = async (p) => { try { await api.post("/admin/os/spaces", p); await load(); toast.success("Rom lagt til"); } catch (e) { feil(e); throw e; } };
  const endreSpace = async (id, p) => { try { await api.patch(`/admin/os/spaces/${id}`, p); await load(); } catch (e) { feil(e); throw e; } };
  const slettSpace = async (sp) => {
    if (!window.confirm(`Slette «${sp.name}»?`)) return;
    try { await api.delete(`/admin/os/spaces/${sp.id}`); await load(); toast.success("Slettet"); } catch (e) { feil(e); }
  };

  const synlige = useMemo(() => {
    if (!data) return [];
    return kunPublisert ? data.sessions.filter((s) => s.published) : data.sessions;
  }, [data, kunPublisert]);

  const aapenSession = useMemo(() => data?.sessions.find((s) => s.id === aapen) || null, [data, aapen]);

  if (!data) {
    return <p className="text-center text-xs text-neutral-400 py-20" data-testid="programos-laster">Laster program …</p>;
  }

  const publiserte = data.sessions.filter((s) => s.published).length;
  const konflikter = data.sessions.filter((s) => (s.conflicts || []).length > 0).length;

  return (
    <div data-testid="programos-admin">
      <header className="flex flex-wrap items-end gap-3 mb-5">
        <div className="mr-auto">
          <p className="adm-eyebrow mb-1.5">Event 2027</p>
          <h1 className="adm-title">Program</h1>
          <p className="adm-sub mt-1.5" data-testid="programos-teller">
            {data.sessions.length} sessions · {publiserte} publisert{konflikter > 0 ? ` · ${konflikter} med romkonflikt` : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setKunPublisert((v) => !v)}
          className={`inline-flex items-center gap-1.5 text-[12px] font-medium rounded-lg px-2.5 py-2 border transition-colors ${
            kunPublisert ? "bg-neutral-900 text-white border-neutral-900" : "text-neutral-500 border-[#E7E4DB] bg-white hover:text-neutral-800"
          }`}
          data-testid="programos-publisert-filter"
        >
          <Eye size={13} /> Kun publisert
        </button>
        <div className="flex bg-white border border-[#E7E4DB] rounded-lg p-0.5">
          {[
            { k: "tidslinje", label: "Tidslinje", Ico: Rows3 },
            { k: "liste", label: "Liste", Ico: List },
          ].map(({ k, label, Ico }) => (
            <button
              key={k}
              type="button"
              onClick={() => setVisning(k)}
              className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-1.5 rounded-md transition-all ${visning === k ? "bg-neutral-900 text-white" : "text-neutral-500 hover:text-neutral-800"}`}
              data-testid={`programos-view-${k}`}
            >
              <Ico size={13} /> {label}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => setVisSpaces(true)} className="adm-btn-ghost" data-testid="programos-rom-btn">
          <DoorOpen size={13} /> Rom
        </button>
        <button type="button" onClick={nySession} className="adm-btn" data-testid="programos-ny-btn">
          <Plus size={14} /> Ny session
        </button>
      </header>

      {visning === "tidslinje" ? (
        <Tidslinje sessions={synlige} spaces={data.spaces} onOpen={setAapen} />
      ) : (
        <div className="adm-card overflow-hidden" data-testid="programos-liste">
          <table className="adm-table">
            <thead>
              <tr>
                <th className="w-24">Tid</th>
                <th>Session</th>
                <th>Format</th>
                <th>Rom</th>
                <th>Status</th>
                <th>Nettside</th>
                <th>Readiness</th>
              </tr>
            </thead>
            <tbody>
              {synlige.map((s) => {
                const rom = data.spaces.find((sp) => sp.id === s.spaceId);
                const st = SSTATUS[s.status] || SSTATUS.draft;
                return (
                  <tr key={s.id} onClick={() => setAapen(s.id)} className="cursor-pointer" data-testid={`programos-rad-${s.id}`}>
                    <td className="tabular-nums text-neutral-500">{s.start || "—"}{s.end ? `–${s.end}` : ""}</td>
                    <td>
                      <span className="font-medium">{s.internalTitle}</span>
                      {(s.conflicts || []).length > 0 && <span className="ml-2 text-[10px] font-bold text-red-600">⚠ konflikt</span>}
                    </td>
                    <td className="text-[11.5px] text-neutral-500">{FORMAT_LABEL[s.format] || s.format}</td>
                    <td className="text-[12px] text-neutral-500">{rom?.name || "—"}</td>
                    <td>
                      <span className="inline-flex items-center gap-1.5 text-[11.5px] text-neutral-600">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.dot }} /> {st.label}
                      </span>
                    </td>
                    <td>{s.published ? <span className="adm-live">Publisert</span> : <span className="adm-draft">Utkast</span>}</td>
                    <td><ReadinessBadge computed={s.computed} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {aapenSession && (
        <SessionDrawer
          sess={aapenSession}
          spaces={data.spaces}
          speakers={data.speakers}
          members={data.members}
          aktor={aktor}
          onPatch={(p) => patch(aapenSession.id, p)}
          onDelete={() => slett(aapenSession.id)}
          onLukk={() => setAapen(null)}
        />
      )}
      {visSpaces && (
        <SpacesModal spaces={data.spaces} onNy={nySpace} onEndre={endreSpace} onSlett={slettSpace} onLukk={() => setVisSpaces(false)} />
      )}
    </div>
  );
}
